// Barreira de recriação do player (WALL 1 — perf/tv-zap-quickwins).
//
// Bug MEDIDO na TV real: reabrir o MESMO canal com dwell~0 (fecha e reabre
// na hora) falha intermitente — o `sign` demora >15s ou nunca sai, ou dá
// `MediaError code 4` + retry. Causa: o teardown (doDestroy) do player
// anterior COLIDE com o load do novo no `<video>` SINGLETON.
//
// Por que colide: cada WebPlayer tem sua PRÓPRIA fila interna (opQueue é
// por instância) — não há serialização ENTRE o player que morre e o que
// nasce. O `runDestroy` do anterior faz `video.pause()` +
// `removeAttribute('src')` + `video.load()` no elemento compartilhado, o
// que invalida o load recém-iniciado do player novo.
//
// Fix: serializar no nível do provider. O player novo só faz attach/load
// DEPOIS que o anterior confirmar o evento `'destroyed'` (fim do
// `runDestroy`: `<video>` já resetado e solto). Esse evento já existe no
// WebPlayer e estava OCIOSO — aqui ele vira o sinal de serialização.

/** Contrato mínimo que a barreira precisa do player anterior. */
export interface DestroyedEmitter {
  on(event: 'destroyed', handler: () => void): void
}

/**
 * Cria a promise-barreira de um destroy em andamento. Resolve quando:
 *
 *  - o player anterior emite `'destroyed'` (caminho feliz — teardown
 *    concluído, `<video>` já resetado), OU
 *  - estoura `timeoutMs` (REDE DE SEGURANÇA: se `'destroyed'` nunca
 *    disparar, NÃO travamos o zap pra sempre — prossegue e aceita o risco
 *    residual). É o guard anti-deadlock exigido pelo plano.
 *
 * Resolve no MÁXIMO uma vez (idempotente): o primeiro entre `'destroyed'`
 * e o timeout ganha; o outro vira no-op.
 */
export function createDestroyBarrier(
  player: DestroyedEmitter,
  timeoutMs: number,
): Promise<void> {
  return new Promise<void>((resolve) => {
    let settled = false
    let handle: ReturnType<typeof setTimeout> | null = null
    const done = (): void => {
      if (settled) return
      settled = true
      if (handle !== null) {
        clearTimeout(handle)
        handle = null
      }
      resolve()
    }
    // Sinal primário: fim real do teardown.
    player.on('destroyed', done)
    // Rede de segurança contra deadlock.
    handle = setTimeout(done, timeoutMs)
  })
}
